import React, { useState, useEffect } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useQuery } from 'react-query';
import apiClient from '../config/axios';
import { XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, Area, AreaChart } from 'recharts';
import { TrendingUp, Mail, Activity, Calendar, Eye, MousePointer, UserX, Target, Clock, BarChart3, Search, Filter } from 'lucide-react';
import { StatisticsOverviewSkeleton, StatisticsPerformanceSkeleton, StatisticsActivitySkeleton, StatisticsUnsubscribesSkeleton } from '../components/Skeleton';
import toast from 'react-hot-toast';

const Statistics = () => {
  const { currentWorkspace } = useWorkspace();
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('30'); // days
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [unsubscribesPage, setUnsubscribesPage] = useState(1);
  const [unsubscribesLimit] = useState(10);
  const [searchFilter, setSearchFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [filteredUnsubscribes, setFilteredUnsubscribes] = useState([]);
  const [activityPage, setActivityPage] = useState(1);
  const [activityLimit] = useState(15);

  // Reset activity page when tab changes
  useEffect(() => {
    if (activeTab === 'activity') {
      setActivityPage(1);
    }
  }, [activeTab]);

  // Fetch email analytics overview
  const { data: overviewData, isLoading: overviewLoading } = useQuery(
    ['emailAnalytics', 'overview', currentWorkspace?.id, dateRange, customDateRange],
    async () => {
      if (!currentWorkspace?.id) return null;

      const headers = { 'x-workspace-id': currentWorkspace.id };
      let params = {};

      if (customDateRange.start && customDateRange.end) {
        params = { start_date: customDateRange.start, end_date: customDateRange.end };
      } else {
        params = { period: dateRange };
      }

      const response = await apiClient.get('/api/email-analytics/overview', { params, headers });
      return response.data;
    },
    {
      enabled: !!currentWorkspace?.id,
      refetchInterval: 30000, // Refresh every 30 seconds
      onError: (error) => {
        console.error('Analytics overview error:', error);
        toast.error('Failed to load analytics data');
      }
    }
  );

  // Fetch campaign performance data
  const { data: performanceData, isLoading: performanceLoading } = useQuery(
    ['emailAnalytics', 'performance', currentWorkspace?.id, dateRange, customDateRange],
    async () => {
      if (!currentWorkspace?.id) return null;

      const headers = { 'x-workspace-id': currentWorkspace.id };
      let params = {};

      if (customDateRange.start && customDateRange.end) {
        params = { start_date: customDateRange.start, end_date: customDateRange.end };
      } else {
        params = { period: dateRange };
      }

      const response = await apiClient.get('/api/email-analytics/campaign-performance', { params, headers });
      return response.data;
    },
    {
      enabled: !!currentWorkspace?.id && activeTab === 'performance',
      onError: (error) => {
        console.error('Performance data error:', error);
        toast.error('Failed to load performance data');
      }
    }
  );

  // Fetch unsubscribe data with pagination and filter for unsubscribed contacts only
  const { data: unsubscribeData, isLoading: unsubscribeLoading } = useQuery(
    ['emailAnalytics', 'unsubscribes', currentWorkspace?.id, dateRange, customDateRange, unsubscribesPage, unsubscribesLimit],
    async () => {
      if (!currentWorkspace?.id) return null;

      const headers = { 'x-workspace-id': currentWorkspace.id };
      let params = {
        page: unsubscribesPage,
        limit: unsubscribesLimit,
        subscription_status: 'unsubscribed' // Only show unsubscribed contacts
      };

      if (customDateRange.start && customDateRange.end) {
        params = { ...params, start_date: customDateRange.start, end_date: customDateRange.end };
      } else {
        params = { ...params, period: dateRange };
      }

      const response = await apiClient.get('/api/email-analytics/unsubscribes', { params, headers });
      return response.data;
    },
    {
      enabled: !!currentWorkspace?.id && activeTab === 'unsubscribes',
      onError: (error) => {
        console.error('Unsubscribe data error:', error);
        toast.error('Failed to load unsubscribe data');
      }
    }
  );

  // Fetch recent activity
  const { data: activityData, isLoading: activityLoading } = useQuery(
    ['emailAnalytics', 'activity', currentWorkspace?.id, activityPage],
    async () => {
      if (!currentWorkspace?.id) return null;

      const headers = { 'x-workspace-id': currentWorkspace.id };
      const params = {
        page: activityPage,
        limit: activityLimit
      };
      const response = await apiClient.get('/api/email-analytics/recent-activity', { headers, params });
      return response.data;
    },
    {
      enabled: !!currentWorkspace?.id,
      refetchInterval: 10000, // Refresh every 10 seconds for activity
      onError: (error) => {
        console.error('Activity data error:', error);
      }
    }
  );

  // Fetch top campaigns
  const { data: topCampaignsData } = useQuery(
    ['emailAnalytics', 'topCampaigns', currentWorkspace?.id, dateRange],
    async () => {
      if (!currentWorkspace?.id) return null;

      const headers = { 'x-workspace-id': currentWorkspace.id };
      const response = await apiClient.get('/api/email-analytics/top-campaigns', {
        params: { period: dateRange, limit: 5 },
        headers
      });
      return response.data;
    },
    {
      enabled: !!currentWorkspace?.id,
      onError: (error) => {
        console.error('Top campaigns data error:', error);
      }
    }
  );

  // Fetch subscription statistics
  const { data: subscriptionData, isLoading: subscriptionLoading } = useQuery(
    ['emailAnalytics', 'subscriptionStats', currentWorkspace?.id],
    async () => {
      if (!currentWorkspace?.id) return null;

      const headers = { 'x-workspace-id': currentWorkspace.id };
      const response = await apiClient.get('/api/email-analytics/subscription-stats', { headers });
      return response.data;
    },
    {
      enabled: !!currentWorkspace?.id,
      refetchInterval: 60000, // Refresh every minute
      onError: (error) => {
        console.error('Subscription stats error:', error);
        toast.error('Failed to load subscription statistics');
      }
    }
  );

  const handleDateRangeChange = (range) => {
    if (range === 'custom') {
      setShowDatePicker(true);
    } else {
      setDateRange(range);
      setCustomDateRange({ start: '', end: '' });
      setShowDatePicker(false);
    }
  };

  const applyCustomDateRange = () => {
    if (customDateRange.start && customDateRange.end) {
      setDateRange('custom');
      setShowDatePicker(false);
    } else {
      toast.error('Please select both start and end dates');
    }
  };

  // Filter unsubscribes data
  useEffect(() => {
    if (!unsubscribeData?.unsubscribes) {
      setFilteredUnsubscribes([]);
      return;
    }

    let filtered = unsubscribeData.unsubscribes;

    // Apply search filter
    if (searchFilter.trim()) {
      filtered = filtered.filter(contact =>
        (contact.contact_name && contact.contact_name.toLowerCase().includes(searchFilter.toLowerCase())) ||
        (contact.email_address && contact.email_address.toLowerCase().includes(searchFilter.toLowerCase())) ||
        (contact.contact_phone && contact.contact_phone.toLowerCase().includes(searchFilter.toLowerCase()))
      );
    }

    // Apply group filter
    if (groupFilter && groupFilter !== 'all') {
      filtered = filtered.filter(contact =>
        contact.group_name === groupFilter ||
        (groupFilter === 'no-group' && (!contact.group_name || contact.group_name === 'No Group'))
      );
    }

    setFilteredUnsubscribes(filtered);
  }, [unsubscribeData?.unsubscribes, searchFilter, groupFilter]);

  // Get unique groups for filter dropdown
  const availableGroups = [...new Set((unsubscribeData?.unsubscribes || [])
    .map(contact => contact.group_name)
    .filter(group => group && group !== 'No Group')
  )];

  // Reset pagination when filters change
  useEffect(() => {
    setUnsubscribesPage(1);
  }, [searchFilter, groupFilter]);

  const overview = overviewData?.overview || {};
  const today = overviewData?.today || {};
  const subscriptionStats = subscriptionData?.subscriptionStats || {};

  const stats = [
    {
      title: 'Total Emails Sent',
      value: overview.totalEmailsSent?.toLocaleString() || '0',
      change: `${today.emailsSent || 0} today`,
      icon: Mail,
      color: 'blue'
    },
    {
      title: 'Open Rate',
      value: `${overview.openRate || 0}%`,
      change: `${today.opens || 0} opens today`,
      icon: Eye,
      color: 'green'
    },
    {
      title: 'Click Rate',
      value: `${overview.clickRate || 0}%`,
      change: `${today.clicks || 0} clicks today`,
      icon: MousePointer,
      color: 'purple'
    },
    {
      title: 'Unsubscribe Rate',
      value: `${overview.unsubscribeRate || 0}%`,
      change: `${today.unsubscribes || 0} today`,
      icon: UserX,
      color: 'orange'
    }
  ];

  const statusData = [
    { name: 'Delivered', value: overview.totalEmailsSent - (overview.totalBounces || 0) - (overview.totalFailures || 0), color: '#10B981' },
    { name: 'Opened', value: overview.uniqueOpens || 0, color: '#3B82F6' },
    { name: 'Clicked', value: overview.uniqueClicks || 0, color: '#8B5CF6' },
    { name: 'Bounced', value: overview.totalBounces || 0, color: '#EF4444' },
    { name: 'Failed', value: overview.totalFailures || 0, color: '#F59E0B' }
  ];

  const renderOverviewTab = () => {
    if (overviewLoading || performanceLoading) {
      return <StatisticsOverviewSkeleton />;
    }

    return (
      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className={`p-3 rounded-lg bg-${stat.color}-100`}>
                  <stat.icon className={`h-6 w-6 text-${stat.color}-600`} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
              </div>
              <div className="mt-4 flex items-center">
                <span className="text-sm text-blue-600 font-medium">{stat.change}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Daily Performance Chart */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Email Performance</h3>
            {performanceData?.dailyData?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={performanceData.dailyData.slice(0, 30).reverse()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="sent" stackId="1" stroke="#3B82F6" fill="#3B82F6" name="Sent" />
                  <Area type="monotone" dataKey="unique_opens" stackId="2" stroke="#10B981" fill="#10B981" name="Opens" />
                  <Area type="monotone" dataKey="unique_clicks" stackId="3" stroke="#8B5CF6" fill="#8B5CF6" name="Clicks" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-500">
                {overviewLoading ? 'Loading...' : 'No data available'}
              </div>
            )}
          </div>

          {/* Email Status Distribution */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Email Status Distribution</h3>
            <div className="flex flex-col lg:flex-row items-start gap-6">
              {/* Pie Chart */}
              <div className="flex-1">
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={statusData.filter(item => item.value > 0)}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {statusData.filter(item => item.value > 0).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => [value, name]}
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Legend */}
              <div className="flex-shrink-0 lg:w-48">
                <div className="space-y-3">
                  {statusData.map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div
                          className="w-4 h-4 rounded-full mr-3 flex-shrink-0"
                          style={{ backgroundColor: item.color }}
                        ></div>
                        <span className="text-sm font-medium text-gray-700">{item.name}</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-900 ml-2">
                        {item.value.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Total */}
                {statusData.some(item => item.value > 0) && (
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-900">Total</span>
                      <span className="text-sm font-bold text-gray-900">
                        {statusData.reduce((sum, item) => sum + item.value, 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Top Campaigns */}
        {topCampaignsData?.topCampaigns?.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Performing Campaigns</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sent</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Open Rate</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Click Rate</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Sent</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {topCampaignsData.topCampaigns.map((campaign) => (
                    <tr key={campaign.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{campaign.name}</div>
                          <div className="text-sm text-gray-500">{campaign.template_subject}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{campaign.total_sent}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{campaign.open_rate}%</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{campaign.click_rate}%</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{campaign.last_sent}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPerformanceTab = () => {
    if (performanceLoading) {
      return <StatisticsPerformanceSkeleton />;
    }

    return (
      <div className="space-y-6">
        {performanceData?.campaignPerformance?.length > 0 ? (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Campaign Performance Details</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sent</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Opens</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clicks</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Open Rate</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Click Rate</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bounces</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {performanceData.campaignPerformance.map((campaign) => (
                    <tr key={campaign.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{campaign.name}</div>
                          <div className="text-sm text-gray-500">{campaign.template_subject}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${campaign.status === 'active' ? 'bg-green-100 text-green-800' :
                          campaign.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                            campaign.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                          }`}>
                          {campaign.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{campaign.total_sent}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{campaign.unique_opens}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{campaign.unique_clicks}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">{campaign.openRate}%</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">{campaign.clickRate}%</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">{campaign.bounces}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-center text-gray-500">
              {performanceLoading ? 'Loading campaign performance data...' : 'No campaign performance data available'}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderUnsubscribesTab = () => {
    if (unsubscribeLoading) {
      return <StatisticsUnsubscribesSkeleton />;
    }

    return (
      <div className="space-y-6">
        {/* Contact Subscription Status Cards */}
        {subscriptionData && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Contact Subscription Status</h3>
              <div className="text-sm text-gray-500">
                {subscriptionLoading ? 'Loading...' : `${subscriptionData?.totals?.total || 0} total contacts`}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Subscribed Contacts */}
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="flex items-center">
                  <div className="p-2 rounded-lg bg-green-100">
                    <Target className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-green-800">Subscribed</p>
                    <p className="text-2xl font-bold text-green-900">{subscriptionData?.totals?.subscribed?.toLocaleString() || '0'}</p>
                  </div>
                </div>
                <div className="mt-2">
                  <span className="text-sm text-green-700 font-medium">
                    {subscriptionData?.totals?.subscriptionRate || 0}% of total
                  </span>
                </div>
              </div>

              {/* Unsubscribed Contacts */}
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <div className="flex items-center">
                  <div className="p-2 rounded-lg bg-red-100">
                    <UserX className="h-5 w-5 text-red-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-red-800">Unsubscribed</p>
                    <p className="text-2xl font-bold text-red-900">{subscriptionData?.totals?.unsubscribed?.toLocaleString() || '0'}</p>
                  </div>
                </div>
                <div className="mt-2">
                  <span className="text-sm text-red-700 font-medium">
                    {subscriptionData?.totals?.total > 0 ? ((subscriptionData?.totals?.unsubscribed || 0) / subscriptionData.totals.total * 100).toFixed(1) : 0}% of total
                  </span>
                </div>
              </div>

              {/* Total Contacts */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <div className="flex items-center">
                  <div className="p-2 rounded-lg bg-blue-100">
                    <Activity className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-blue-800">Total Contacts</p>
                    <p className="text-2xl font-bold text-blue-900">{subscriptionData?.totals?.total?.toLocaleString() || '0'}</p>
                  </div>
                </div>
                <div className="mt-2">
                  <span className="text-sm text-blue-700 font-medium">
                    {subscriptionData?.recentChanges?.length || 0} recent changes
                  </span>
                </div>
              </div>
            </div>

          </div>
        )}

        {/* Unsubscribe Stats with Email Campaigns Integration */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-red-100">
                <UserX className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Unsubscribes</p>
                <p className="text-2xl font-bold text-gray-900">{overview.totalUnsubscribes || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-orange-100">
                <TrendingUp className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Unsubscribe Rate</p>
                <p className="text-2xl font-bold text-gray-900">{overview.unsubscribeRate || 0}%</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-blue-100">
                <Clock className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Today</p>
                <p className="text-2xl font-bold text-gray-900">{today.unsubscribes || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-green-100">
                <Target className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Still Subscribed</p>
                <p className="text-2xl font-bold text-gray-900">{subscriptionStats.totals?.subscribed || 0}</p>
              </div>
            </div>
          </div>
        </div>


        {/* Unsubscribed Contacts List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <h3 className="text-lg font-semibold text-gray-900">Unsubscribed Contacts</h3>
              <div className="text-sm text-gray-500">
                {filteredUnsubscribes.length} of {unsubscribeData?.pagination?.total || 0} contacts
              </div>
            </div>

            {/* Filter Controls */}
            <div className="mt-4 flex flex-col sm:flex-row gap-4">
              {/* Search Filter */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search by name, email, or phone..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>

              {/* Group Filter */}
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <select
                  value={groupFilter}
                  onChange={(e) => setGroupFilter(e.target.value)}
                  className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm appearance-none bg-white min-w-[150px]"
                >
                  <option value="">All Groups</option>
                  {availableGroups.map(group => (
                    <option key={group} value={group}>{group}</option>
                  ))}
                  <option value="no-group">No Group</option>
                </select>
              </div>

              {/* Clear Filters */}
              {(searchFilter || groupFilter) && (
                <button
                  onClick={() => {
                    setSearchFilter('');
                    setGroupFilter('');
                  }}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>

          {unsubscribeLoading ? (
            <div className="p-6 text-center text-gray-500">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              Loading unsubscribed contacts...
            </div>
          ) : filteredUnsubscribes?.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Group</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredUnsubscribes
                      .slice((unsubscribesPage - 1) * unsubscribesLimit, unsubscribesPage * unsubscribesLimit)
                      .map((unsubscribe) => (
                        <tr key={unsubscribe.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center mr-3">
                                <UserX className="h-4 w-4 text-red-600" />
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">{unsubscribe.contact_name || 'N/A'}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{unsubscribe.email_address}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{unsubscribe.contact_phone || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{unsubscribe.group_name || 'No Group'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                              Unsubscribed
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{unsubscribe.unsubscribed_at}</div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination Controls */}
              {filteredUnsubscribes.length > 0 && (
                <div className="px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-500">
                      Showing {Math.min(((unsubscribesPage - 1) * unsubscribesLimit) + 1, filteredUnsubscribes.length)} to{' '}
                      {Math.min(unsubscribesPage * unsubscribesLimit, filteredUnsubscribes.length)} of{' '}
                      {filteredUnsubscribes.length} filtered results
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setUnsubscribesPage(prev => Math.max(prev - 1, 1))}
                        disabled={unsubscribesPage === 1}
                        className={`px-3 py-1 rounded-md text-sm font-medium ${unsubscribesPage === 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                          }`}
                      >
                        Previous
                      </button>

                      <span className="text-sm text-gray-700">
                        Page {unsubscribesPage} of {Math.ceil(filteredUnsubscribes.length / unsubscribesLimit)}
                      </span>

                      <button
                        onClick={() => setUnsubscribesPage(prev => Math.min(prev + 1, Math.ceil(filteredUnsubscribes.length / unsubscribesLimit)))}
                        disabled={unsubscribesPage === Math.ceil(filteredUnsubscribes.length / unsubscribesLimit)}
                        className={`px-3 py-1 rounded-md text-sm font-medium ${unsubscribesPage === Math.ceil(filteredUnsubscribes.length / unsubscribesLimit)
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                          }`}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="p-6 text-center text-gray-500">
              <UserX className="h-12 w-12 text-gray-300 mx-auto mb-2" />
              <p>
                {searchFilter || groupFilter
                  ? 'No contacts match the current filters'
                  : 'No unsubscribed contacts found for this period'
                }
              </p>
              {(searchFilter || groupFilter) && (
                <button
                  onClick={() => {
                    setSearchFilter('');
                    setGroupFilter('');
                  }}
                  className="mt-2 text-blue-600 hover:text-blue-800 text-sm"
                >
                  Clear filters to see all contacts
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderRecentActivityTab = () => {
    if (activityLoading) {
      return <StatisticsActivitySkeleton />;
    }

    return (
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Recent Email Activity</h3>
        </div>
        <div className="p-6">
          {activityData?.recentActivity?.length > 0 ? (
            <div className="space-y-4">
              {activityData.recentActivity.map((activity, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className={`p-2 rounded-full ${activity.activity_type === 'email_sent' ? 'bg-blue-100' :
                      activity.activity_type === 'email_opened' ? 'bg-green-100' :
                        activity.activity_type === 'email_clicked' ? 'bg-purple-100' :
                          'bg-red-100'
                      }`}>
                      {activity.activity_type === 'email_sent' ? <Mail className="h-4 w-4 text-blue-600" /> :
                        activity.activity_type === 'email_opened' ? <Eye className="h-4 w-4 text-green-600" /> :
                          activity.activity_type === 'email_clicked' ? <MousePointer className="h-4 w-4 text-purple-600" /> :
                            <UserX className="h-4 w-4 text-red-600" />}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {activity.activity_type === 'email_sent' ? 'Email Sent' :
                        activity.activity_type === 'email_opened' ? 'Email Opened' :
                          activity.activity_type === 'email_clicked' ? 'Email Clicked' :
                            'Unsubscribed'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {activity.campaign_name} - {activity.contact_name || 'Unknown'}
                    </p>
                    <p className="text-xs text-gray-400">
                      📧 {activity.email_address}
                      {activity.url && (
                        <span className="ml-2">
                          🔗 <span className="text-blue-600">{activity.url}</span>
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <p className="text-sm text-gray-500">{activity.activity_time}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500">
              {activityLoading ? 'Loading recent activity...' : 'No recent activity'}
            </div>
          )}

          {/* Pagination Controls */}
          {activityData?.pagination && activityData.pagination.totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
              <div className="text-sm text-gray-500">
                Showing page {activityData.pagination.currentPage} of {activityData.pagination.totalPages}
                ({activityData.pagination.totalCount} total activities)
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setActivityPage(prev => Math.max(1, prev - 1))}
                  disabled={!activityData.pagination.hasPrev}
                  className={`px-3 py-1 text-sm rounded ${activityData.pagination.hasPrev
                    ? 'bg-blue-500 text-white hover:bg-blue-600'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                >
                  Previous
                </button>
                <span className="flex items-center px-3 py-1 text-sm text-gray-600">
                  Page {activityData.pagination.currentPage}
                </span>
                <button
                  onClick={() => setActivityPage(prev => prev + 1)}
                  disabled={!activityData.pagination.hasNext}
                  className={`px-3 py-1 text-sm rounded ${activityData.pagination.hasNext
                    ? 'bg-blue-500 text-white hover:bg-blue-600'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-Between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Email Campaign Analytics
            {currentWorkspace && (
              <span className="text-sm font-normal text-gray-500 ml-2">
                - {currentWorkspace.name}
              </span>
            )}
          </h1>
        </div>

        {/* Date Range Filter */}
        <div className="mt-4 sm:mt-0 flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Calendar className="h-5 w-5 text-gray-400" />
            <select
              value={dateRange}
              onChange={(e) => handleDateRangeChange(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
              <option value="custom">Custom range</option>
            </select>
          </div>
        </div>
      </div>

      {/* Custom Date Picker */}
      {showDatePicker && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={customDateRange.start}
                onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={customDateRange.end}
                onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={applyCustomDateRange}
              className="mt-6 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
            >
              Apply
            </button>
            <button
              onClick={() => setShowDatePicker(false)}
              className="mt-6 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'performance', label: 'Campaign Performance', icon: Target },
            { id: 'unsubscribes', label: 'Unsubscribes', icon: UserX },
            { id: 'activity', label: 'Recent Activity', icon: Activity }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <tab.icon className="h-5 w-5" />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {overviewLoading && activeTab === 'overview' ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading analytics data...</div>
        </div>
      ) : (
        <>
          {activeTab === 'overview' && renderOverviewTab()}
          {activeTab === 'performance' && renderPerformanceTab()}
          {activeTab === 'unsubscribes' && renderUnsubscribesTab()}
          {activeTab === 'activity' && renderRecentActivityTab()}
        </>
      )}
    </div>
  );
};

export default Statistics;
